"use client";

import { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Calendar } from "../calendar";
import { DateInput } from "./date-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/catalyst/button";
import dayjs from "dayjs";

export type DateRange = {
  from: Date;
  to: Date | undefined;
};

interface Preset {
  name: string;
  label: string;
}

export interface DateRangePickerProps {
  onUpdate?: (values: { range: DateRange }) => void;
  initialDateFrom?: Date | string;
  initialDateTo?: Date | string;
  align?: "start" | "center" | "end";
  locale?: string;
  className?: string;
  disableFutureDates?: boolean;
}

// Define presets
const PRESETS: Preset[] = [
  { name: "last7", label: "Last 7 days" },
  { name: "last30", label: "Last 30 days" },
  { name: "last365", label: "Last 365 days" },
  { name: "thisWeek", label: "This Week" },
  { name: "lastWeek", label: "Last Week" },
  { name: "thisMonth", label: "This Month" },
  { name: "lastMonth", label: "Last Month" },
  { name: "thisYear", label: "This Year" },
  { name: "lastYear", label: "Last Year" },
];

/**
 * Formats a date according to the specified locale
 */
const formatDate = (date: Date, locale: string = "en-us"): string => {
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Adjusts a date string or Date object for timezone differences
 */
const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === "string") {
    const parts = dateInput.split("-").map((part) => parseInt(part, 10));
    // Month is 0-indexed in JavaScript Date
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }
  return dateInput;
};

/**
 * Gets date range for a specific preset
 */
const getPresetRange = (presetName: string): DateRange => {
  const preset = PRESETS.find(({ name }) => name === presetName);
  if (!preset) throw new Error(`Unknown date range preset: ${presetName}`);

  let from: dayjs.Dayjs;
  let to: dayjs.Dayjs;

  switch (preset.name) {
    case "last7":
      from = dayjs().subtract(6, "day").startOf("day");
      to = dayjs().endOf("day");
      break;
    case "last30":
      from = dayjs().subtract(29, "day").startOf("day");
      to = dayjs().endOf("day");
      break;
    case "last365":
      from = dayjs().subtract(364, "day").startOf("day");
      to = dayjs().endOf("day");
      break;
    case "thisWeek":
      from = dayjs().startOf("week");
      to = dayjs().endOf("day");
      break;
    case "lastWeek":
      from = dayjs().subtract(1, "week").startOf("week");
      to = dayjs().subtract(1, "week").endOf("week");
      break;
    case "thisMonth":
      from = dayjs().startOf("month");
      to = dayjs().endOf("day");
      break;
    case "lastMonth":
      from = dayjs().subtract(1, "month").startOf("month");
      to = dayjs().subtract(1, "month").endOf("month");
      break;
    case "thisYear":
      from = dayjs().startOf("year");
      to = dayjs().endOf("day");
      break;
    case "lastYear":
      from = dayjs().subtract(1, "year").startOf("year");
      to = dayjs().subtract(1, "year").endOf("year");
      break;
    default:
      throw new Error(`Unknown date range preset: ${presetName}`);
  }

  return {
    from: from.toDate(),
    to: to.toDate(),
  };
};

/**
 * Checks if two date ranges are equal
 */
const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
  if (!a || !b) return a === b;
  return (
    a.from.getTime() === b.from.getTime() &&
    (!a.to || !b.to || a.to.getTime() === b.to.getTime())
  );
};

export function DateRangePicker({
  initialDateFrom = new Date(new Date().setHours(0, 0, 0, 0)),
  initialDateTo,
  onUpdate,
  align = "end",
  locale = "en-US",
  className,
  disableFutureDates = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 960 : false,
  );

  const initialToDate = initialDateTo
    ? getDateAdjustedForTimezone(initialDateTo)
    : getDateAdjustedForTimezone(initialDateFrom);

  const [range, setRange] = useState<DateRange>({
    from: getDateAdjustedForTimezone(initialDateFrom),
    to: initialToDate,
  });

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(
    undefined,
  );

  // Store the values of range when the date picker is opened
  const openedRangeRef = useRef<DateRange | null>(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check which preset matches the current range
  useEffect(() => {
    checkPreset();
  }, [range]);

  // Store initial range when popover opens
  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range;
    }
  }, [isOpen]);

  /**
   * Set date range based on preset
   */
  const setPreset = (preset: string): void => {
    setRange(getPresetRange(preset));
  };

  /**
   * Check if current range matches any preset
   */
  const checkPreset = (): void => {
    for (const preset of PRESETS) {
      const presetRange = getPresetRange(preset.name);

      const normalizedRangeFrom = new Date(range.from);
      normalizedRangeFrom.setHours(0, 0, 0, 0);

      const normalizedPresetFrom = new Date(presetRange.from);
      normalizedPresetFrom.setHours(0, 0, 0, 0);

      const normalizedRangeTo = new Date(range.to ?? 0);
      normalizedRangeTo.setHours(0, 0, 0, 0);

      const normalizedPresetTo = new Date(presetRange.to ?? 0);
      normalizedPresetTo.setHours(0, 0, 0, 0);

      if (
        normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
        normalizedRangeTo.getTime() === normalizedPresetTo.getTime()
      ) {
        setSelectedPreset(preset.name);
        return;
      }
    }

    setSelectedPreset(undefined);
  };

  /**
   * Reset to initial values
   */
  const resetValues = (): void => {
    setRange({
      from: getDateAdjustedForTimezone(initialDateFrom),
      to: initialToDate,
    });
  };

  /**
   * Render preset buttons for desktop view
   */
  const renderPresetButtons = () => (
    <div className="flex w-full flex-col items-end gap-1 pb-6 pl-6 pr-2">
      {PRESETS.map((preset) => {
        const isSelected = selectedPreset === preset.name;
        return (
          <Button
            key={preset.name}
            className={cn(isSelected && "pointer-events-none", className)}
            plain
            onClick={() => setPreset(preset.name)}
          >
            <>
              <span
                className={cn("pr-2 opacity-0", isSelected && "opacity-70")}
              >
                <CheckIcon width={18} height={18} />
              </span>
              {preset.label}
            </>
          </Button>
        );
      })}
    </div>
  );

  /**
   * Render preset dropdown for mobile view
   */
  const renderPresetDropdown = () => (
    <Select
      defaultValue={selectedPreset}
      onValueChange={(value) => setPreset(value)}
    >
      <SelectTrigger className="mx-auto mb-2 w-[180px]">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {PRESETS.map((preset) => (
          <SelectItem key={preset.name} value={preset.name}>
            {preset.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const displayDateRange = `${formatDate(range.from, locale)}${
    range.to ? " - " + formatDate(range.to, locale) : ""
  }`;

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          resetValues();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button outline>
          <div className="text-right">
            <div>{displayDateRange}</div>
          </div>
          <div className="-mr-2 scale-125 pl-1 opacity-60">
            <ChevronDownIcon
              width={24}
              className={cn("transition-transform", {
                "rotate-180": isOpen,
              })}
            />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto">
        <div className="flex py-2">
          <div className="flex">
            <div className="flex flex-col">
              <div className="flex flex-col items-center justify-end gap-2 px-3 pb-4 lg:flex-row lg:items-start lg:pb-0">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <DateInput
                      value={range.from}
                      onChange={(date) => {
                        const toDate =
                          range.to == null || date > range.to ? date : range.to;
                        setRange((prevRange) => ({
                          ...prevRange,
                          from: date,
                          to: toDate,
                        }));
                      }}
                    />
                    <div className="py-1">-</div>
                    <DateInput
                      value={range.to}
                      onChange={(date) => {
                        const fromDate = date < range.from ? date : range.from;
                        setRange((prevRange) => ({
                          ...prevRange,
                          from: fromDate,
                          to: date,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>

              {isSmallScreen && renderPresetDropdown()}

              <div>
                <Calendar
                  showOutsideDays={false}
                  mode="range"
                  styles={{ day: { minWidth: "36px" } }}
                  classNames={{
                    today: "",
                  }}
                  onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                    if (value?.from) {
                      setRange({ from: value.from, to: value?.to });
                    }
                  }}
                  selected={range}
                  numberOfMonths={isSmallScreen ? 1 : 2}
                  disabled={
                    disableFutureDates
                      ? (date) => dayjs(date).isAfter()
                      : undefined
                  }
                  defaultMonth={
                    new Date(
                      new Date().setMonth(
                        new Date().getMonth() - (isSmallScreen ? 0 : 1),
                      ),
                    )
                  }
                />
              </div>
            </div>
          </div>

          {!isSmallScreen && renderPresetButtons()}
        </div>

        <div className="flex justify-end gap-2 py-2 pr-4">
          <Button
            onClick={() => {
              setIsOpen(false);
              resetValues();
            }}
            outline
          >
            Cancel
          </Button>
          <Button
            color="indigo"
            onClick={() => {
              setIsOpen(false);
              if (!areRangesEqual(range, openedRangeRef.current as DateRange)) {
                onUpdate?.({ range });
              }
            }}
          >
            Update
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
